/* remy-search.js -- HAND-AUTHORED (theme-src/, HANDOFF §38).
   Store-only: the header search overlay on Shopify's predictive search.

   Markup lives in layout/theme.liquid (#remy-search, data-remy-search-*).
   Results are server-rendered by sections/predictive-search.liquid and
   fetched through the Section Rendering API, so the cards are the theme's
   one product card, not a JS copy of it.

   Request discipline
     - debounced ~200 ms; an empty query sends nothing and clears the panel
     - each new request aborts the previous one (AbortController), AND every
       response is checked against a sequence number and the query it was for,
       so an out-of-order or stale response can never be painted
     - while a request is in flight the old results stay where they are,
       dimmed; the meta line keeps its min-height, so nothing jumps

   Keys: Esc closes and returns focus to whatever opened it. Tab is trapped
   inside the panel while it is open (it is aria-modal). Enter is NOT
   suppressed: it submits the form to /search?q=…&type=product.

   script.js (verbatim /mockup copy) has its own static search bound to
   #search-overlay; that id no longer exists in the layout, so it stays
   inert. Do not reintroduce it. */
(function () {
  'use strict';

  var panel = document.getElementById('remy-search');
  if (!panel) return;

  var form    = panel.querySelector('form');
  var input   = panel.querySelector('[data-remy-search-input]');
  var meta    = panel.querySelector('[data-remy-search-meta]');
  var results = panel.querySelector('[data-remy-search-results]');
  var scrim   = document.querySelector('.search-scrim');
  var base    = panel.getAttribute('data-predictive-url') || '/search/suggest';

  var LIMIT = 10;              // Shopify's predictive-search maximum
  var DEBOUNCE = 200;
  var timer = null, ctrl = null, seq = 0, opener = null;

  /* ---- open / close (same motion as the static overlay) ---------------- */
  function show(el) {
    if (!el) return;
    el.hidden = false;
    void el.offsetWidth;                       // flush so the transition runs
    el.classList.add('is-open');
  }
  function setExpanded(v) {
    document.querySelectorAll('[data-remy-search-open]').forEach(function (b) {
      b.setAttribute('aria-expanded', v ? 'true' : 'false');
    });
  }

  function open(from) {
    opener = from || document.activeElement;
    show(scrim);
    show(panel);
    document.body.style.overflow = 'hidden';
    setExpanded(true);
    input.focus();
    input.select();
  }

  function close() {
    if (panel.hidden) return;
    panel.classList.remove('is-open');
    if (scrim) scrim.classList.remove('is-open');
    document.body.style.overflow = '';
    setExpanded(false);
    setTimeout(function () {
      if (panel.classList.contains('is-open')) return;
      panel.hidden = true;
      if (scrim) scrim.hidden = true;
    }, 260);
    if (opener && opener.focus) opener.focus();
  }

  document.addEventListener('click', function (e) {
    var o = e.target.closest('[data-remy-search-open]');
    if (o) { e.preventDefault(); open(o); return; }
    if (e.target.closest('[data-remy-search-close]')) close();
  });

  document.addEventListener('keydown', function (e) {
    if (panel.hidden) return;
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab') return;
    var f = Array.prototype.filter.call(
      panel.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"])'),
      function (el) { return el.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    else if (!panel.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
  });

  /* ---- querying -------------------------------------------------------- */
  function busy(on) {
    results.style.opacity = on ? '.45' : '';
    results.style.transition = 'opacity .15s';
    if (on) results.setAttribute('aria-busy', 'true');
    else results.removeAttribute('aria-busy');
  }

  function clear() {
    seq++;                                     // orphan anything in flight
    if (ctrl) ctrl.abort();
    ctrl = null;
    busy(false);
    results.innerHTML = '';
    meta.textContent = '';
  }

  function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }

  function paint(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var resp = doc.querySelector('[data-remy-search-response]');
    if (!resp) throw new Error('no predictive-search section in response');
    var n = parseInt(resp.getAttribute('data-count'), 10) || 0;
    var c = parseInt(resp.getAttribute('data-collections'), 10) || 0;
    if (n) {
      meta.textContent = n >= LIMIT
        ? 'Top ' + n + ' results — press Enter for all'
        : plural(n, 'result', 'results');
    } else if (c) {
      meta.textContent = plural(c, 'collection', 'collections');
    } else {
      meta.textContent = 'No results';
    }
    results.innerHTML = resp.innerHTML;
  }

  function run(q) {
    var my = ++seq;
    if (ctrl) ctrl.abort();
    ctrl = window.AbortController ? new AbortController() : null;

    var url = base + '?q=' + encodeURIComponent(q) +
      '&section_id=predictive-search' +
      '&resources[type]=product,collection' +
      '&resources[limit]=' + LIMIT +
      '&resources[limit_scope]=each' +         // 10 products AND the collections
      '&resources[options][unavailable_products]=last';

    busy(true);
    meta.textContent = 'Searching…';
    fetch(url, { credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (my !== seq) return;                 // a newer request owns the panel
        busy(false);
        paint(html);
      })
      .catch(function (err) {
        if (my !== seq || (err && err.name === 'AbortError')) return;
        busy(false);
        results.innerHTML = '';
        meta.textContent = 'Search is unavailable — press Enter to search';
      });
  }

  input.addEventListener('input', function () {
    clearTimeout(timer);
    var q = input.value.trim();
    if (!q) { clear(); return; }
    timer = setTimeout(function () { run(q); }, DEBOUNCE);
  });

  // An empty query has nowhere useful to go: keep the panel open instead.
  form.addEventListener('submit', function (e) {
    if (!input.value.trim()) { e.preventDefault(); input.focus(); }
  });
}());
